import {
    afterRenderEffect,
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    signal,
    viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { firstValueFrom, map } from 'rxjs';

import { LcDialog } from '../../../shared/dialog/dialog';

import {
    formatToolLabel,
    markdownToSafeHtml,
    splitThinkingSegments,
    ThinkingSegments,
    type ChatLine,
} from '../ai-chat-message.util';
import { AiChatPageContextService } from '../services/ai-chat-page-context.service';
import { AiChatPanelService } from '../services/ai-chat-panel.service';
import { AiChatThreadService, type ChatThreadSummary } from '../services/ai-chat-thread.service';
import { AiChatService, type NavigationAction } from '../services/ai-chat.service';

type ToolChip = {
    name: string;
    label: string;
    status: 'running' | 'done';
    durationMs?: number;
};

const STARTER_PROMPTS = [
    { label: 'Find a customer', message: 'Help me find a customer by name' },
    { label: 'List services', message: 'List available services' },
    { label: 'Advanced search', message: 'Search customers with service filters' },
] as const;

const CLEAR_CONTEXT_CONFIRM = {
    data: {
        title: 'Clear conversation context?',
        message:
            'This wipes the current thread history. The thread stays open but the assistant forgets prior messages.',
        confirmActionText: 'Clear',
        denyActionText: 'Cancel',
    },
};

const DELETE_THREAD_CONFIRM = {
    data: {
        title: 'Delete this chat?',
        message: 'This permanently deletes the selected thread and its messages.',
        confirmActionText: 'Delete',
        denyActionText: 'Cancel',
    },
};

/** Cap composer textarea growth (~5 lines of form-control-sm). */
const MAX_COMPOSER_HEIGHT_PX = 120;

@Component({
    selector: 'acm-ai-chat-popup',
    imports: [FormsModule, MatIcon, NgbDropdownModule],
    templateUrl: './ai-chat-popup.component.html',
    styleUrl: './ai-chat-popup.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        id: 'ai-chat-popup',
        class: 'ai-chat-popup d-flex flex-column',
        role: 'dialog',
        'aria-labelledby': 'ai-chat-popup-title',
        '(click)': 'onHostClick($event)',
    },
})
export class AiChatPopupComponent {
    protected readonly threads = inject(AiChatThreadService);
    protected readonly panel = inject(AiChatPanelService);
    protected readonly starterPrompts = STARTER_PROMPTS;

    private readonly chat = inject(AiChatService);
    private readonly pageContext = inject(AiChatPageContextService);
    private readonly dialog = inject(LcDialog);

    protected readonly messages = signal<ChatLine[]>([]);
    protected readonly toolChips = signal<ToolChip[]>([]);
    protected readonly toolPanelOpen = signal(true);
    protected readonly thinkingOpenByIndex = signal<ReadonlySet<number>>(new Set());
    protected readonly streamingThinkingOpen = signal(false);
    protected readonly navigationActions = signal<NavigationAction[]>([]);
    protected readonly isStreaming = signal(false);
    protected readonly streamingText = signal('');
    protected readonly contextWarning = signal(false);
    protected draft = '';

    protected readonly showStarters = computed(
        () =>
            this.messages().length === 0 && !this.isStreaming() && this.streamingText().length === 0
    );

    protected readonly isInputBlocked = computed(() => this.threads.isContextBlocked());

    protected readonly streamingSegments = computed(() =>
        splitThinkingSegments(this.streamingText(), { streaming: true })
    );

    protected readonly sortedThreads = computed(() =>
        [...this.threads.threads()].sort(
            (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        )
    );

    private readonly messagesPane = viewChild<ElementRef<HTMLElement>>('messagesPane');
    private readonly composerInput = viewChild<ElementRef<HTMLTextAreaElement>>('composerInput');

    constructor() {
        afterRenderEffect(() => {
            this.streamingText();
            this.messages();
            this.streamingThinkingOpen();
            const pane = this.messagesPane()?.nativeElement;
            if (pane == null) {
                return;
            }
            pane.scrollTop = pane.scrollHeight;
        });
    }

    protected onComposerKeydown(event: KeyboardEvent): void {
        if (event.isComposing || event.key === 'Unidentified') {
            return;
        }
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            void this.send();
        }
    }

    protected onComposerInput(event: Event): void {
        const target = event.target;
        if (!(target instanceof HTMLTextAreaElement)) {
            return;
        }
        this.resizeComposer(target);
    }

    private resizeComposer(textarea: HTMLTextAreaElement): void {
        textarea.style.height = 'auto';
        textarea.style.height = `${Math.min(textarea.scrollHeight, MAX_COMPOSER_HEIGHT_PX)}px`;
    }

    private collapseComposer(): void {
        const textarea = this.composerInput()?.nativeElement;
        if (textarea == null) {
            return;
        }
        this.resizeComposer(textarea);
    }

    protected activeThreadTitle(): string {
        const activeId = this.threads.activeThreadId();
        if (activeId == null) {
            return 'New chat';
        }
        const match = this.threads.threads().find((thread) => thread.threadId === activeId);
        if (match == null) {
            return 'New chat';
        }
        return this.threads.threadTitle(match);
    }

    protected async sendPreset(message: string): Promise<void> {
        this.draft = message;
        await this.send();
    }

    protected async send(): Promise<void> {
        const text = this.draft.trim();
        if (text.length === 0 || this.isStreaming() || this.isInputBlocked()) {
            return;
        }

        this.draft = '';
        // Collapse after ngModel clears the textarea DOM (same-tick scrollHeight is stale).
        requestAnimationFrame(() => this.collapseComposer());
        this.messages.update((lines) => [...lines, { role: 'user', text }]);
        this.navigationActions.set([]);
        this.contextWarning.set(false);
        this.isStreaming.set(true);
        this.streamingText.set('');
        this.toolChips.set([]);

        let assistantText = '';
        const threadId = await this.threads.ensureActiveThread();

        try {
            await this.chat.streamMessage(threadId, text, this.pageContext.current(), (event) => {
                if (event.type === 'delta') {
                    assistantText += event.text;
                    this.streamingText.set(assistantText);
                } else if (event.type === 'tool_start') {
                    this.toolChips.update((chips) => [
                        ...chips.filter((chip) => chip.name !== event.name),
                        {
                            name: event.name,
                            label: formatToolLabel(event.name),
                            status: 'running',
                        },
                    ]);
                } else if (event.type === 'tool_end') {
                    this.toolChips.update((chips) =>
                        chips.map((chip) =>
                            chip.name === event.name
                                ? {
                                      ...chip,
                                      status: 'done',
                                      durationMs: (chip.durationMs ?? 0) + event.durationMs,
                                  }
                                : chip
                        )
                    );
                } else if (event.type === 'navigation') {
                    this.navigationActions.set(event.actions);
                } else if (event.type === 'context_warning') {
                    this.contextWarning.set(true);
                    this.threads.contextUtilization.set(event.utilization);
                } else if (event.type === 'done') {
                    this.threads.contextUtilization.set(event.contextUtilization);
                    void this.threads.refreshThreads();
                } else if (event.type === 'error') {
                    assistantText = event.message;
                    this.streamingText.set(assistantText);
                }
            });
        } catch (error) {
            assistantText = (error as Error).message;
            this.streamingText.set(assistantText);
        } finally {
            this.isStreaming.set(false);
            this.streamingThinkingOpen.set(false);
            if (assistantText.length > 0) {
                this.messages.update((lines) => [
                    ...lines,
                    { role: 'assistant', text: assistantText },
                ]);
            }
            this.streamingText.set('');
        }
    }

    protected async switchThread(thread: ChatThreadSummary): Promise<void> {
        if (this.isStreaming()) {
            return;
        }
        const lines = await this.threads.switchThread(thread.threadId);
        this.messages.set(lines);
        this.thinkingOpenByIndex.set(new Set());
        this.navigationActions.set([]);
        this.contextWarning.set(this.threads.contextUtilization() >= 0.5);
        this.toolChips.set([]);
    }

    protected async createThread(): Promise<void> {
        if (this.isStreaming()) {
            return;
        }
        await this.threads.createThread();
        this.resetToNewChatUi();
    }

    protected async deleteCurrentThread(): Promise<void> {
        if (this.isStreaming()) {
            return;
        }
        const threadId = this.threads.activeThreadId();
        if (threadId == null) {
            return;
        }
        const confirmed = await firstValueFrom(
            this.dialog.confirm(DELETE_THREAD_CONFIRM).closed.pipe(map(Boolean))
        );
        if (!confirmed) {
            return;
        }
        await this.threads.deleteThread(threadId);
        this.resetToNewChatUi();
    }

    protected async clearContext(): Promise<void> {
        if (this.isStreaming()) {
            return;
        }
        const confirmed = await firstValueFrom(
            this.dialog.confirm(CLEAR_CONTEXT_CONFIRM).closed.pipe(map(Boolean))
        );
        if (!confirmed) {
            return;
        }
        await this.threads.clearContext();
        this.resetToNewChatUi();
    }

    private resetToNewChatUi(): void {
        this.messages.set([]);
        this.thinkingOpenByIndex.set(new Set());
        this.navigationActions.set([]);
        this.contextWarning.set(false);
        this.toolChips.set([]);
        this.draft = '';
        this.streamingText.set('');
    }

    protected navigate(action: NavigationAction): void {
        this.chat.navigate(action);
        // Keep the overlay open across SPA navigations triggered from the chat.
        this.panel.open();
    }

    /**
     * Intercept left-clicks on in-app markdown links so the browser does not do a full
     * document load (which would destroy the open chat overlay). Modified clicks (new tab)
     * and external URLs keep the default behavior.
     */
    protected onHostClick(event: MouseEvent): void {
        if (
            event.defaultPrevented ||
            event.button !== 0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey
        ) {
            return;
        }
        const target = event.target;
        if (!(target instanceof Element)) {
            return;
        }
        const anchor = target.closest('a[href]');
        if (!(anchor instanceof HTMLAnchorElement)) {
            return;
        }
        const href = anchor.getAttribute('href');
        if (href == null || href.length === 0) {
            return;
        }
        if (!this.chat.navigateInAppUrl(href)) {
            return;
        }
        event.preventDefault();
        this.panel.open();
    }

    protected utilizationPercent(): number {
        return Math.round(this.threads.contextUtilization() * 100);
    }

    protected toggleToolPanel(): void {
        this.toolPanelOpen.update((open) => !open);
    }

    protected toggleThinking(index: number): void {
        this.thinkingOpenByIndex.update((open) => {
            const next = new Set(open);
            if (next.has(index)) {
                next.delete(index);
            } else {
                next.add(index);
            }
            return next;
        });
    }

    protected toggleStreamingThinking(): void {
        this.streamingThinkingOpen.update((open) => !open);
    }

    protected isThinkingOpen(index: number): boolean {
        return this.thinkingOpenByIndex().has(index);
    }

    protected segmentsFor(text: string): ThinkingSegments {
        return splitThinkingSegments(text);
    }

    protected responseHtml(markdown: string): string {
        return markdownToSafeHtml(markdown);
    }
}
